import uniq from 'lodash/uniq';
import without from 'lodash/without';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import { InventoryTransferForm } from '../../../../constants/constant_form_names';
import {
  PENDING,
  PICK,
  PACK,
  SHIP,
  RECEIVED,
  CANCELLED,
} from '../../../../constants/constant_inventory_status';
import {
  OPEN,
  PENDING_APPROVAL,
  PROCESSING,
  CLOSED,
  CLOSED_ABANDONED,
} from '../../../../constants/constant_transaction_status';
import { BATCH, SERIALIZED } from '../../../../constants/constant_item_types';
import { getInventoryStatus, getTransactionStatus } from '../../../../common/selectors/selector_common';
const selector = formValueSelector(InventoryTransferForm);

let INVENTORY_STATUS_BY_TRANSACTION_STATUS = {};
INVENTORY_STATUS_BY_TRANSACTION_STATUS[OPEN] = [PENDING];
INVENTORY_STATUS_BY_TRANSACTION_STATUS[PENDING_APPROVAL] = [PENDING];
INVENTORY_STATUS_BY_TRANSACTION_STATUS[PROCESSING] = [
  PENDING,
  PICK,
  PACK,
  SHIP,
  RECEIVED,
  CANCELLED,
];
INVENTORY_STATUS_BY_TRANSACTION_STATUS[CLOSED] = [RECEIVED, CANCELLED];
INVENTORY_STATUS_BY_TRANSACTION_STATUS[CLOSED_ABANDONED] = [PENDING, CANCELLED];

function getFormId(state) {
  const { _id, id } = selector(state, '_id', 'id');
  return _id || id;
}

function getFormSaleTransactionRef(state) {
  return selector(state, 'sale_transaction_id');
}

function getFormStatus(state) {
  return selector(state, 'status') || {};
}

function getPropViewMode(state, viewMode) {
  return viewMode;
}

function getPropWithViewMode(state, index, viewMode) {
  return viewMode;
}

function getFormSelectedInventoryStatus(state) {
  return selector(state, 'checkbox_inventory_status');
}

function getSettings(state) {
  return state.inventory.transfer.settings;
}

function getFormCurrency(state) {
  return selector(state, 'from_currency') || {};
}

function getFormBrandIds(state) {
  return selector(state, `brand_ids`) || [];
}

function getFormClassificationIds(state) {
  return selector(state, `classification_ids`) || [];
}

function getFormAlternateClassIds(state) {
  return selector(state, `alternate_classification_ids`) || [];
}

function getInventoryTransfer(state) {
  return state.inventory.transfer;
}

function getFormFromLocation(state) {
  return selector(state, 'from_location') || {};
}

function getFromSubsidiary(state) {
  return selector(state, 'from_subsidiary') || {};
}

function getFormItemLines(state) {
  return selector(state, 'lines') || [];
}

function getFormItemLineByIndex(state, index) {
  return selector(state, `lines[${index}]`) || {};
}

function getFormApplyToLocation(state) {
  return selector(state, 'apply_to_location');
}

function getFormApplyStatus(state) {
  return selector(state, 'apply_status');
}

function getFormRunStatus(state) {
  return selector(state, 'run_status');
}

function getFormReferenceId(state) {
  return selector(state, 'reference_id');
}

function getFormIsFetchingItemLocations(state) {
  return selector(state, 'isFetchingItemLocations');
}

export const isFetchingItemLocations = createSelector(
  [getFormIsFetchingItemLocations],
  (isFetchingItemLocations) => isFetchingItemLocations,
);

/**
 * Filter and sort inventory status depending on saved status
 */
export const getFilteredInventoryStatus = createSelector(
  [getInventoryStatus, getFormStatus],
  (inventoryStatusList, transactionStatus) => {
    let list = [];

    if (!inventoryStatusList) return list;

    const transactionStatusCid = transactionStatus.cid;

    //Filter depending on transaction status
    list = inventoryStatusList.filter((inventoryStatus) => {
      const inventoryStatusCid = inventoryStatus.cid;

      if (transactionStatusCid) {
        return (
          INVENTORY_STATUS_BY_TRANSACTION_STATUS[transactionStatusCid].indexOf(
            inventoryStatusCid,
          ) > -1
        );
      }

      return inventoryStatusCid === PENDING || inventoryStatusCid === CANCELLED;
    });

    list = sortBy(list, (inventoryStatus) => {
      const inventoryStatusCid = inventoryStatus.cid;
      switch (inventoryStatusCid) {
        case PENDING:
          return 0;
        case PICK:
          return 1;
        case PACK:
          return 2;
        case SHIP:
          return 3;
        case RECEIVED:
          return 4;
        case CANCELLED:
          return 5;
        default:
          return 0;
      }
    });

    return list;
  },
);

export const getAllowedInventoryStatus = createSelector(
  [getFilteredInventoryStatus, getSettings, getFormSelectedInventoryStatus],
  (inventoryStatusList, settings, selectedInventoryStatus) => {
    let list = [];
    if (!inventoryStatusList) return list;

    const { use_status_pick, use_status_pack, use_status_ship } = settings;

    //Remove inventory status depending on settings
    const filteredList = inventoryStatusList.filter((inventoryStatus) => {
      const inventoryStatusCid = inventoryStatus.cid;
      if (
        inventoryStatusCid === PICK &&
        !use_status_pick &&
        selectedInventoryStatus !== PICK
      )
        return false;
      else if (
        inventoryStatusCid === PACK &&
        !use_status_pack &&
        selectedInventoryStatus !== PACK
      )
        return false;
      else if (
        inventoryStatusCid === SHIP &&
        !use_status_ship &&
        selectedInventoryStatus !== SHIP
      )
        return false;
      else if (
        inventoryStatusCid === CANCELLED &&
        selectedInventoryStatus === RECEIVED
      )
        return false;
      else if (
        inventoryStatusCid === RECEIVED &&
        selectedInventoryStatus === CANCELLED
      )
        return false;
      return true;
    });

    if (selectedInventoryStatus === CANCELLED) {
      list = [...filteredList];
    } else {
      let selectedStatusIndex = -1;

      filteredList.forEach((inventoryStatus, index) => {
        const inventoryStatusCid = inventoryStatus.cid;
        console.log('inventoryStatusCid', inventoryStatusCid);
        console.log('selectedInventoryStatus', selectedInventoryStatus);

        if (inventoryStatusCid === selectedInventoryStatus)
          selectedStatusIndex = index;
      });
      console.log('filteredList', filteredList[selectedStatusIndex - 1]);
      console.log('filteredList', filteredList[selectedStatusIndex + 1]);

      if (filteredList[selectedStatusIndex - 1]) {
        list.splice(0, 0, filteredList[selectedStatusIndex - 1]);
      }
      if (filteredList[selectedStatusIndex + 1]) {
        list.splice(2, 0, filteredList[selectedStatusIndex + 1]);
      }
      console.log('list', list);

      //Get cancelled inventory status
      if (
        selectedInventoryStatus === PENDING ||
        selectedInventoryStatus === PICK ||
        selectedInventoryStatus === PACK ||
        selectedInventoryStatus === SHIP
      ) {
        list.push(filteredList[filteredList.length - 1]);
      }
    }
    list = uniq(list, (a) => {
      return a.id;
    });

    return list;
  },
);

export const getLineOnEditMode = createSelector(
  [getFormItemLineByIndex],
  (line) => {
    const { onEditMode } = line;
    return onEditMode;
  },
);

export const showLineLoader = createSelector(
  [getFormItemLineByIndex],
  (line) => {
    return line.isFetchingItemLocation;
  },
);

export const getMassTransferProgress = createSelector(
  [getInventoryTransfer],
  (inventoryTransfer) => {
    return inventoryTransfer.massTransferInProgress;
  },
);

export const isInterCompanyTransfer = createSelector(
  [getFormItemLines, getFromSubsidiary, getFormApplyToLocation],
  (lines, fromSubsidiary, applyToLocation) => {
    if (!lines.length) return false;

    if (applyToLocation) {
      const { subsidiary: applyToLocationSubsidiary } = applyToLocation;
      const toSubsidiaryId = (applyToLocationSubsidiary || {}).id;
      return Boolean(toSubsidiaryId && toSubsidiaryId !== fromSubsidiary.id);
    }

    const isInterCompany = lines.some((line) => {
      const toSubsidiaryId = line.to_subsidiary_id;
      return toSubsidiaryId && toSubsidiaryId !== fromSubsidiary.id;
    });

    return isInterCompany;
  },
);

export const hasOnEditModeLine = createSelector([getFormItemLines], (lines) => {
  if (!lines.length) return false;
  return lines.some((line) => {
    return line && !line.quantity_transfer && !line.item_id && line.onEditMode;
  });
});

export const getEditModeLineIndex = createSelector(
  [getFormItemLines],
  (lines) => {
    if (!lines.length) return null;
    return lines.findIndex((line) => {
      return line && line.onEditMode;
    });
  },
);

export const containsReceivedStatus = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.some((line) => (line.status || {}).cid === RECEIVED);
  },
);

export const containsReceivedNextStatus = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.some((line) => (line.next_status || {}).cid === RECEIVED);
  },
);

export const isSerializedItem = createSelector(
  [getFormItemLineByIndex],
  (line) => {
    return (line.item_type || line.item_type_id) === SERIALIZED;
  },
);

export const getLineItemUom = createSelector(
  [getFormItemLineByIndex],
  (line) => line.item_uom,
);

export const hasBatchSerializedItems = createSelector(
  [getFormItemLines],
  (lines) => {
    if (!lines.length) return false;
    return lines.some((line) => {
      const itemTypeId = line.item_type_id;
      return itemTypeId === BATCH || itemTypeId === SERIALIZED;
    });
  },
);

export const isClosed = createSelector([getFormStatus], (transactionStatus) => {
  const transactionStatusCid = (transactionStatus || {}).cid;
  return (
    transactionStatusCid === CLOSED || transactionStatusCid === CLOSED_ABANDONED
  );
});

export const isProcessing = createSelector(
  [getFormStatus],
  (transactionStatus) => {
    const transactionStatusCid = (transactionStatus || {}).cid;
    return transactionStatusCid === PROCESSING;
  },
);

export const isFromSaleTx = createSelector(
  [getFormSaleTransactionRef],
  (transactionReference) => {
    return Boolean(transactionReference);
  },
);

export const hasSelectedLines = createSelector([getFormItemLines], (lines) => {
  if (!lines) return false;
  return lines.some((line) => {
    return line.process_line;
  });
});

export const hasRunStatus = createSelector([getFormRunStatus], (runStatus) => {
  return Boolean(runStatus);
});

export const applyStatusReceived = createSelector(
  [getFormApplyStatus],
  (applyStatus) => {
    return (applyStatus || {}).cid === RECEIVED;
  },
);

export const applyStatusPick = createSelector(
  [getFormApplyStatus],
  (applyStatus) => {
    return applyStatus === PICK;
  },
);

export const isOpen = createSelector([getFormStatus], (transactionStatus) => {
  return transactionStatus.cid === OPEN;
});

export const getUnfilledLineItemIndex = createSelector(
  [getFormItemLines],
  (lines) => {
    return lines.findIndex((line) => {
      return line && !line.quantity_transfer && !line.item_id;
    });
  },
);

export const getCurrencyCode = createSelector([getFormCurrency], (currency) => {
  return (currency || {}).code || 'USD';
});

export const getCurrencySymbol = createSelector(
  [getFormCurrency],
  (currency) => {
    return (currency || {}).symbol || '$';
  },
);

export const viewModeForm = createSelector(
  [getFormStatus, hasRunStatus, getPropViewMode],
  (status, hasRunStatus, viewMode) => {
    return (
      [PROCESSING, CLOSED, CLOSED_ABANDONED].indexOf(status.cid) > -1 ||
      hasRunStatus ||
      viewMode
    );
  },
);

export const getExcludedToLocations = createSelector(
  [getFormFromLocation],
  (fromLocation) => {
    return fromLocation.id ? [fromLocation.id] : [];
  },
);

export const showApplyLocation = createSelector(
  [getFormStatus, hasSelectedLines, getPropViewMode],
  (status, hasSelectedLines, viewMode) => {
    return (status || {}).cid === OPEN && hasSelectedLines && !viewMode;
  },
);

export const showApplyStatus = createSelector(
  [getFormStatus, hasSelectedLines, getPropViewMode],
  (status, hasSelectedLines, viewMode) => {
    return (status || {}).cid === PROCESSING && hasSelectedLines && !viewMode;
  },
);

export const showSelectButtons = createSelector(
  [hasRunStatus, isClosed, getPropViewMode],
  (hasRunStatus, isClosed, viewMode) => {
    return !hasRunStatus && !isClosed && !viewMode;
  },
);

export const getLinesZeroQtyIndexes = createSelector(
  [getFormItemLines],
  (lines) => {
    const removeIndexes = [];
    lines.forEach((line, index) => {
      if (!line.quantity_available) removeIndexes.unshift(index);
    });

    return removeIndexes;
  },
);

export const getSelectedLines = createSelector([getFormItemLines], (lines) => {
  const selectedIndexes = [];
  lines.forEach((line, index) => {
    if (line.process_line) {
      selectedIndexes.unshift({
        index,
        quantity_transfer: line.quantity_transfer,
      });
    }
  });

  return selectedIndexes;
});

export const getGroupedLineByStatus = createSelector(
  [getFormItemLines],
  (itemLines) => {
    const groupedLinesByStatus = groupBy(
      itemLines,
      (itemLine) => itemLine.status.cid,
    );
    const keys = Object.keys(groupedLinesByStatus);

    keys.forEach((key) => {
      const groupedLines = groupedLinesByStatus[key];
      const lines = [];

      groupedLines.forEach((line) => {
        const lineId = line.line_id;
        const lineStatusId = line.status.cid;

        if ([RECEIVED, CANCELLED].indexOf(lineStatusId) === -1) {
          lines.push({
            index: itemLines.findIndex(
              (itemLine) => itemLine.line_id === lineId,
            ),
            statusName: (line.status || {}).name,
          });
        }
      });

      groupedLinesByStatus[key] = lines;
    });

    return groupedLinesByStatus;
  },
);

export const getFilterBrands = createSelector([getFormBrandIds], (brands) => {
  return brands;
});

export const getFilterClassifications = createSelector(
  [getFormClassificationIds],
  (classifications) => {
    return classifications;
  },
);

export const getFilterAlternateClass = createSelector(
  [getFormAlternateClassIds],
  (alternateClass) => {
    return alternateClass;
  },
);

export const getLineId = createSelector([getFormItemLineByIndex], (line) => {
  const { line_id } = line;
  return line_id;
});

export const getInventoryStatusList = createSelector(
  [getInventoryStatus],
  (statusList) => statusList,
);

export const getTransactionStatusList = createSelector(
  [getTransactionStatus],
  (statusList) => statusList,
);

export const getDefaultLineStatus = createSelector(
  [getInventoryStatus],
  (statusList) => statusList.find((status) => status.cid === PENDING),
);

export const getDefaultTransactionStatus = createSelector(
  [getTransactionStatus],
  (statusList) => statusList.find((status) => status.cid === OPEN),
);

export const getTransactionStatusClosedAbandoned = createSelector(
  [getTransactionStatus],
  (statusList) => statusList.find((status) => status.cid === CLOSED_ABANDONED),
);

export const getTransactionStatusProcessing = createSelector(
  [getTransactionStatus],
  (statusList) => statusList.find((status) => status.cid === PROCESSING),
);

export const hasLineNextStatus = createSelector(
  [getFormItemLineByIndex],
  (line) => {
    const { next_status } = line;
    return Boolean((next_status || {}).cid);
  },
);

export const getSystemMessage = createSelector(
  [getFormItemLineByIndex],
  (line) => line.system_message,
);

export const showReceiveRelatedColumns = createSelector(
  [getFormApplyStatus, containsReceivedNextStatus, containsReceivedStatus],
  (applyStatus, containsReceivedNextStatus, containsReceivedStatus) => {
    return (
      (applyStatus || {}).cid === RECEIVED ||
      containsReceivedNextStatus ||
      containsReceivedStatus
    );
  },
);

export const getLineStatus = createSelector(
  [getFormItemLineByIndex],
  (line) => {
    return line.status.cid;
  },
);

export const disableLineCheckbox = createSelector(
  [getFormSelectedInventoryStatus, getLineStatus],
  (selectedInventoryStatus, status) => {
    return selectedInventoryStatus ? status !== selectedInventoryStatus : false;
  },
);

export const isExistingTransfer = createSelector([getFormId], (id) => {
  return Boolean(id);
});

export const isLineForProcessing = createSelector(
  [getFormItemLineByIndex],
  (line) => {
    return Boolean(line.process_line);
  },
);

export const isLineBatchSerializedItem = createSelector(
  [getFormItemLineByIndex],
  (line) => {
    const itemTypeId = line.item_type_id;
    return [BATCH, SERIALIZED].indexOf(itemTypeId) > -1;
  },
);

export const isLineReceivedStatus = createSelector(
  [getLineStatus],
  (status) => {
    return status === RECEIVED;
  },
);

export const isLineReceivedNextStatus = createSelector(
  [getFormItemLineByIndex],
  (line) => {
    return (line.next_status || {}).cid === RECEIVED;
  },
);

export const showLineCheckbox = createSelector(
  [
    hasRunStatus,
    isClosed,
    getPropWithViewMode,
    isLineReceivedStatus,
    isLineReceivedNextStatus,
  ],
  (
    hasRunStatus,
    isClosed,
    viewMode,
    isLineReceivedStatus,
    isLineReceivedNextStatus,
  ) => {
    return (
      !hasRunStatus &&
      !isClosed &&
      !viewMode &&
      !isLineReceivedStatus &&
      !isLineReceivedNextStatus
    );
  },
);

export const showLineReceiveFields = createSelector(
  [
    applyStatusReceived,
    isLineForProcessing,
    isLineReceivedStatus,
    isLineReceivedNextStatus,
  ],
  (
    applyStatusReceived,
    forProcessing,
    isLineReceivedStatus,
    isLineReceivedNextStatus,
  ) => {
    if (
      isLineReceivedStatus ||
      isLineReceivedNextStatus ||
      (applyStatusReceived && forProcessing)
    ) {
      return true;
    }
    return false;
  },
);

export const getSelectedInventoryStatus = createSelector(
  [getFormSelectedInventoryStatus],
  (selectedInventoryStatus) => selectedInventoryStatus,
);

export const getLineQuantityTransfer = createSelector(
  [getFormItemLineByIndex],
  (line) => line.quantity_transfer || 0,
);

export const getLineQuantityAvailable = createSelector(
  [getFormItemLineByIndex],
  (line) => line.quantity_available || 0,
);

export const getIncludedLocations = createSelector(
  [
    isInterCompanyTransfer,
    getFormApplyToLocation,
    getFromSubsidiary,
    getFormItemLines,
  ],
  (isInterCompanyTransfer, applyToLocation, fromSubsidiary, lines) => {
    if (isInterCompanyTransfer) {
      const itemLine =
        lines.find((line) => {
          const toLocationSubsidiaryId = line.to_subsidiary_id;
          return (
            toLocationSubsidiaryId &&
            toLocationSubsidiaryId !== fromSubsidiary.id
          );
        }) || {};

      const itemLineLocationId = itemLine.to_location_id;
      const applyLocationId = (applyToLocation || {}).id;
      const locationId = applyLocationId || itemLineLocationId;
      return locationId ? [locationId] : [];
    }
    return [];
  },
);

export const getIncludedSubsidiary = createSelector(
  [isInterCompanyTransfer, getFromSubsidiary, getFormItemLines],
  (isInterCompanyTransfer, fromSubsidiary, lines) => {
    const hasToLocation = lines.some((line) => {
      return line.to_location_id;
    });
    if (isInterCompanyTransfer && hasToLocation) {
      return [fromSubsidiary.id];
    }
    return [];
  },
);

export const getExcludedItems = createSelector(
  [getFormItemLines, getFormItemLineByIndex],
  (lines, ownLine) => {
    const selectedItemIds = [];
    lines.forEach((line) => {
      const itemId = line.item_id;
      const itemTypeId = line.item_type_id;
      if ([SERIALIZED, BATCH].indexOf(itemTypeId) === -1) {
        selectedItemIds.push(itemId);
      }
    });
    const itemId = ownLine.item_id;
    const excludedItemIds = without(
      selectedItemIds,
      undefined,
      null,
      '',
      itemId,
    );
    return excludedItemIds;
  },
);

export const getReferenceId = createSelector(
  [getFormReferenceId],
  (referenceId) => {
    return referenceId;
  },
);

export const getFromLocation = createSelector(
  [getFormFromLocation],
  (fromLocation) => fromLocation,
);

export const getFromLocationId = createSelector(
  [getFormFromLocation],
  (fromLocation) => fromLocation.id,
);

export const getFormLines = createSelector(
  [getFormItemLines],
  (lines) => lines,
);

export const hasLines = createSelector([getFormItemLines], (lines) =>
  Boolean(lines.length),
);

export const isPartQuantity = createSelector(
  [getFormItemLineByIndex, getSettings],
  (line, preference) => {
    const { item } = line;
    const { enable_item_part_quantity } = preference;
    return enable_item_part_quantity && (item || {}).enable_part_quantity;
  },
);
